import {
  Box,
  Button,
  Flex,
  Input,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Text,
} from "@chakra-ui/react";
import { getZapSplits } from "applesauce-common/helpers";
import { NostrEvent } from "nostr-tools";
import { useForm } from "react-hook-form";

import { humanReadableLits } from "../../helpers/lightning";
import useUserLNURLMetadata from "../../hooks/use-user-lnurl-metadata";
import { EmbedEventCard } from "../embed-event/card";
import { LightningIcon } from "../icons";
import UserAvatar from "../user/user-avatar";
import UserLink from "../user/user-link";

// Logarithmic slider mapping: 1000 steps → 1–100,000 range
const SLIDER_MAX = 1000;
const VALUE_MAX = 100000;
const LOG_MAX = Math.log(VALUE_MAX);

function sliderPosToValue(pos: number): number {
  if (pos <= 0) return 1;
  return Math.round(Math.exp((pos / SLIDER_MAX) * LOG_MAX));
}

function valueToSliderPos(val: number): number {
  if (val <= 1) return 0;
  return Math.round((Math.log(val) / LOG_MAX) * SLIDER_MAX);
}

function UserCard({ pubkey, percent }: { pubkey: string; percent?: number }) {
  const { address } = useUserLNURLMetadata(pubkey);

  return (
    <Flex gap="2" alignItems="center" overflow="hidden">
      <UserAvatar pubkey={pubkey} size="md" />
      <Box overflow="hidden">
        <UserLink pubkey={pubkey} fontWeight="bold" />
        <Text isTruncated>{address}</Text>
      </Box>
      {percent && (
        <Text fontWeight="bold" fontSize="lg" ml="auto">
          {Math.round(percent * 10000) / 100}%
        </Text>
      )}
    </Flex>
  );
}

export type InputStepProps = {
  pubkey: string;
  event?: NostrEvent;
  initialComment?: string;
  initialAmount?: number;
  allowComment?: boolean;
  showEmbed?: boolean;
  isNutzap?: boolean;
  onSubmit: (values: { amount: number; comment: string }) => void;
};

const sliderMarkStyle = {
  mt: "2",
  fontSize: "xs",
  color: "GrayText",
} as const;

export default function InputStep({
  event,
  pubkey,
  initialComment,
  initialAmount,
  allowComment = true,
  showEmbed = true,
  isNutzap = false,
  onSubmit,
}: InputStepProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<{
    amount: number;
    comment: string;
  }>({
    mode: "onBlur",
    defaultValues: {
      amount: initialAmount ?? 50,
      comment: initialComment ?? "",
    },
  });

  const splits = event ? (getZapSplits(event) ?? []) : [];

  const { metadata: lnurlMetadata } = useUserLNURLMetadata(pubkey);
  const canZap = lnurlMetadata?.allowsNostr && lnurlMetadata?.nostrPubkey;

  const showComment = allowComment && (isNutzap || splits.length > 0 || canZap || lnurlMetadata?.commentAllowed);
  const actionName = isNutzap ? "Lit" : canZap ? "Lit" : "Tip";

  const amount = watch("amount");
  const sliderPos = valueToSliderPos(amount);

  const onSubmitZap = handleSubmit(onSubmit);

  return (
    <form onSubmit={onSubmitZap}>
      <Flex gap="4" direction="column">
        {splits.map((p) => (
          <UserCard key={p.pubkey} pubkey={p.pubkey} percent={p.percent} />
        ))}

        {showEmbed && event && <EmbedEventCard event={event} />}

        {showComment && (
          <Input
            placeholder="Comment"
            {...register("comment", { maxLength: lnurlMetadata?.commentAllowed ?? 150 })}
            autoComplete="off"
          />
        )}

        <Flex justifyContent="center" alignItems="baseline" gap="1">
          <Text fontSize="3xl" fontWeight="bold">
            {humanReadableLits(amount)}
          </Text>
          <Text fontSize="md" color="GrayText">
            lits
          </Text>
        </Flex>

        <Box px="2" pb="6">
          <Slider
            min={0}
            max={SLIDER_MAX}
            step={1}
            value={sliderPos}
            onChange={(pos) => setValue("amount", sliderPosToValue(pos), { shouldDirty: true })}
          >
            <SliderMark value={0} {...sliderMarkStyle} ml="-1">
              1
            </SliderMark>
            <SliderMark value={200} {...sliderMarkStyle} ml="-1">
              10
            </SliderMark>
            <SliderMark value={400} {...sliderMarkStyle} ml="-2.5">
              100
            </SliderMark>
            <SliderMark value={600} {...sliderMarkStyle} ml="-2">
              1K
            </SliderMark>
            <SliderMark value={800} {...sliderMarkStyle} ml="-3">
              10K
            </SliderMark>
            <SliderMark value={1000} {...sliderMarkStyle} ml="-4">
              100K
            </SliderMark>
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={6} />
          </Slider>
        </Box>

        <Button leftIcon={<LightningIcon />} type="submit" isLoading={isSubmitting} w="full" autoFocus>
          {actionName} {humanReadableLits(amount)} lits
        </Button>
      </Flex>
    </form>
  );
}
